import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Facebook } from "lucide-react";
import { BrandGraphic } from "@/components/BrandGraphic";
import api from "@/lib/api";

/**
 * Homepage preview: "Life at Garland Ebenezer"
 * Shows 6–8 selected uploaded photos with a VIEW GALLERY button.
 * When no photos uploaded yet, falls back to a clean copy-only block + VIEW GALLERY button.
 */
export const GalleryPreview = () => {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/gallery")
      .then((r) => setPhotos((r.data || []).slice(0, 8)))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return null;

  return (
    <section data-testid="gallery-preview-section" className="py-24 lg:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-12 gap-6">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#D4A437] mb-4">
              Gallery
            </div>
            <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-[#003B5C] leading-tight">
              Life at Garland Ebenezer.
            </h2>
            <p className="mt-5 text-base sm:text-lg text-[#1E1E1E]/70 max-w-2xl leading-relaxed">
              A glimpse of worship, fellowship, ministry, and the everyday life of our church family.
            </p>
          </div>
          <Link
            to="/gallery"
            data-testid="gallery-preview-cta"
            className="inline-flex items-center gap-2 bg-[#003B5C] text-white px-7 py-3.5 rounded-sm text-xs font-bold tracking-[0.18em] uppercase hover:bg-[#002A42] transition-all self-start sm:self-auto group"
          >
            View Gallery
            <ArrowUpRight size={14} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
          </Link>
        </div>

        {photos.length === 0 ? (
          <div data-testid="gallery-preview-empty" className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3">
            {["logo", "monogram", "stripes", "logo", "monogram", "stripes", "logo", "monogram"].map((variant, i) => (
              <div key={`placeholder-${variant}-${i}`} className="aspect-square overflow-hidden rounded-sm">
                <BrandGraphic variant={variant} />
              </div>
            ))}
          </div>
        ) : (
          <div data-testid="gallery-preview-grid" className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {photos.map((p) => (
              <Link
                key={p.id}
                to="/gallery"
                data-testid={`gallery-preview-photo-${p.id}`}
                className="aspect-square overflow-hidden bg-[#F7F8FA] rounded-sm group"
              >
                <img
                  src={p.url}
                  alt={p.caption || "Church life"}
                  loading="lazy"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                />
              </Link>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};
